// Dynamic Plot code copyright 2015-2023 (c) Tuva Labs Inc.
//
// created by Craig D. Miller
// progressIndicator -- progress indicator and animation smoother for the Dynamic plotting code.
//


export const wrapDynProgressIndicator = function( Dyn) {

/**
 * ProgressIndicator for Raphael animation tasks that are slow.
 *
 * We use rafael animations to keep this progress indicator in sync with other animations,
 * so that if one slows down the others will also slow down and show slower progress.
 *
 * Also handles update tasks in batches, based on the number of case icons to update,
 * since case icon updating is the slowest and most variable part of updating a plot.
 *
 * @param {Raphael.paper} paper - view to draw into
 * @constructor
 */
class ProgressIndicator {
        constructor( paper ) {
        // Raphael elements
        this.paper = paper;		// Dyn.plotView.paper;
        this.cycleTimers = []; // for animation cycles
        this.stopTimers = []; // for delayed disappearance of indicator

        // timing parameters
        this.startTimeMS = 0;	// start time when running, 0 when not running, in milliseconds
        this.lastStartMS = 0;	// start time of last start (not reset when stopped)
        this.cycleNumber = 0;	// number of cycles elapsed
        this.cycleEndMS = 0;	// end time of last cycle in milliseconds
        this.stopDelayMS = 0;	// delay the next stop by this many milliseconds to sync with other animations.
        this.taskQueue = [];    // queue of task functions to run, once per cycle, until all tasks done and removed from queue.
        this.stopFunction = null;	// task to run at stop time, once
        this.iconsPerCycle = Dyn.plotUtil.stdIconsPerCycle;
        this.debugInitialIconsPerCycle = 0;
        this.debugCyclesWithTasksCount = 0;
        this.fractionDone = 0;	// [0-1]
        this.flags = {};		// flags set after start() to indicate progress
        if( Dyn.debugUndo ) {
            this.debugging = true; // turn on extra console messages
        }
    }

    // constants used to tune progress indicator
    static allowTaskQueueing = true;	// normally true, turn off for timing tests without task queueing
    static allIconsPerCycle = Dyn.mathUtil.MAX_SAFE_INTEGER;
    static cycleWaitTimeMS = 100; // wait period between each task call (in milliseconds), also animation length
    static progressTimeOutMS = 120000; // 120 seconds (2 minutes)
    static debugCycles = Dyn.debugPlotUpdates;
    static debugTaskEnd = Dyn.debugPlotUpdates;

    destroy() {
        this.stop( "destroy", true ); // cancel old task if any

        // clear references
        this.paper = null;
    }

	/**
	 * Start a progress indicator, centered at the given coordinates in the paper.
	 * @param {number} centerPoint {{ x, y }} -- center of indicator, in view coordinates
	 * @param {boolean} showImmediateProgress - if restarting progress indicator, keep indicator visible even during 0% phase
     * @param {number} estimatedNumIcons -- estimated number of icons (0+) to get starting fraction
	 */
	start( centerPoint, showImmediateProgress=false, estimatedNumIcons=0 ) {
        const showIndicatorDelayMS = Dyn.plotUtil.progressIndicatorDelayMS; // default 500ms delay before showing indicator
		let prog = this;

		if( this.isRunning() ) {
			this.stop( "cancel before we re-start", true ); // cancel old task if any
		}

		// start timing
		this.startTimeMS = Date.now();
		//console.log("Dyn.ProgressIndicator start() ========== ID "+this.startTimeMS);
		this.lastStartMS = this.startTimeMS; // time of last start (for getting the time after stop())
		this.cycleEndMS = this.startTimeMS;	// time of last animation cycle (for getting cycle time)
		this.stopDelayMS = 0;
		this.cycleNumber = this.debugCyclesWithTasksCount = 0;
        // show indicator at 10% not 0%
        //  -- if restarting,
        //  -- or if starting with showImmediateProgress
        //  -- (consider it a rough estimate, 0% is misleading, since work has been done already)
        //  -- NOTE: our goal is to show a good progress amount without having the progress indicator go backwards,
        //      so show 10% if think we can, otherwise less (1-9%)
		this.fractionDone = 0;
		if( showImmediateProgress ) {
            const   firstBatchEstimatedFraction = Dyn.plotUtil.stdIconsPerCycle / Math.max( estimatedNumIcons, 1 ),
                    maxStartingFraction = 0.10;
            this.fractionDone = Math.min( maxStartingFraction, firstBatchEstimatedFraction )
        }
		this.iconsUpdated = 0;
		this.flags = {}; // clear all flags
        this.flags.showImmediateProgress = showImmediateProgress;
        prog.flags.needsOneMoreCycle = true; // don't stop on first task

		animationCycle(); // start animation cycles / tasks

		function animationCycle() {
			var elapsedTimeMS = prog.elapsedTime(),
				//betweenCycleTimeMS = Math.max(0, elapsedTimeMS - prog.cycleEndMS),
				timeOut = ( elapsedTimeMS > ProgressIndicator.progressTimeOutMS),
				percentDone = Dyn.mathUtil.ceiling( prog.fractionDone * 100 ),
								// show % indicator from 1% to 100%, but keep hidden if we go from 0 to 100% in one step
				showIndicator = ((percentDone > 0 ) && (percentDone < 100 ) && elapsedTimeMS >= showIndicatorDelayMS) ||
								(percentDone >= 100 && prog.flags.indicatorHasAppeared),
                didTask = false;

			++(prog.cycleNumber);

			if( ! timeOut ) {
				// Do one cycle, then repeat until done.
				//		console.log("animationCycle() start cycle at "+elapsedTimeMS+" %="+percentDone);
				// (1) show indicator, if not first cycle
				if( showIndicator ) {
                    Dyn.mediator.publish( "plot:indicateProgress", {
                        progress: percentDone
                    });
					prog.flags.indicatorHasAppeared = true;
				}
                ProgressIndicator.debugCycles && console.log("ProgressIndicator animationCycle", (prog.cycleNumber-1), elapsedTimeMS+"ms",
                    (prog.flags.indicatorHasAppeared ? "show:":"hide:")+percentDone+"%", "icons:"+prog.iconsUpdated );

				// (2) run task, if there is one
				if( prog.taskQueue[0] ) {
				    ++(prog.debugCyclesWithTasksCount);
					let tasksDone = (prog.taskQueue[0])(); // perform task, return true if done
					if( tasksDone ) {
					    prog.taskQueue.shift(); // remove the finished task, keep others
						prog.flags.needsOneMoreCycle = true; // to show 100% complete
					}
                    didTask = true;
				}

				// (3) update cycle stats for timing tests
				prog.cycleEndMS = prog.elapsedTime();
				//let inCycleTimeMS = Math.max( 0, prog.cycleEndMS - elapsedTimeMS );
				let keepGoing = ( prog.flags.needsOneMoreCycle || prog.hasTasks() );
				//console.log( "Dyn.ProgressIndicator animationCycle="+prog.cycleNumber
				//  + " at "+prog.cycleEndMS+" ms"
                //    + " between+mid="+betweenCycleTimeMS +"+"+ inCycleTimeMS
                //    + " icons="+prog.iconsUpdated
                //    + " indicatorHasAppeared="+Boolean(prog.flags.indicatorHasAppeared)
                //    + " hasTaskFunction="+didTask
                //    + " re-queue="+keepGoing
                //);

				// (4) keep timer going for another cycle, if not done
				if( keepGoing ) {
                    prog.cycleTimers.push( setTimeout( animationCycle, ProgressIndicator.cycleWaitTimeMS ));
					prog.flags.needsOneMoreCycle = false;
				} else {
					prog.stop( 'finished animationCycle task' ); // stop now
				}
			}
			else {  // stop now
				if( prog.isRunning()) {
					prog.stop( "timeout of animationCycle" ); // long-running animation causes this
				} else {
					// NOTE: many calls to start() in a row, say by dragging an axis endpoint,
					// each cause animationCycle() callback to be queued.  After a normal stop(),
					// 'finished animationCycle task', the elapsedTime() will become huge, and the
					// queued callbacks will all get here. So we do nothing.  For debugging,
					// enable the console log to see how many times we get here.
					//console.log("animationCycle() timer stopped" );
				}
			}
		}
	}

	elapsedTime() {;
		return Date.now() - this.startTimeMS;
	}

	isRunning() {
		return( this.startTimeMS !== 0 );
	}

	/** get the number of icons to animate per task cycle */
	getIconsPerCycle() {
		// reset at start, then increase every time, for slow immediate start then faster finish
		var numIcons = this.iconsPerCycle;

		this.iconsPerCycle = Math.round( this.iconsPerCycle * Dyn.plotUtil.iconCycleExponent );
		return numIcons;
	}

	/**
	 *
	 * @param {number} fractionDone -- fraction done [0-1] to update progress bar
	 */
	showProgress( fractionDone, iconsUpdated ) {
		this.fractionDone = fractionDone;
		this.iconsUpdated = iconsUpdated;
	}

	// re-layer the visible progress indicator so it is in front (like Raphael.element.toFront())
	toFront() {
        //   no longer needed since using bar indicator
	}

    hasTasks() {
       return this.taskQueue.length > 0;
    }

	/**
	 * Run the function after the current progress indicator cycle completes.
	 * @param taskFunction -- task function to call for each animation cycle
	 * @param totalNumIcons how many icons to animate for this task
	 * @param taskMultiplier - [1+] do more icons per cycle than standard? e.g. 10 = 10x more.
	 * @returns {boolean} -- true if task was queued.
	 */
	queueTask( taskFunction, totalNumIcons=0, taskMultiplier=1 ) {
		Dyn.assert( typeof taskFunction === "function" );
		//Dyn.assert( this.taskQueue.length === 0 ); // we allow multiple tasks now
		var queueIt,
            nonIconTask = ( totalNumIcons < 1 );

		// get initial icons per cycle, which is updated+accelerated by getIconsPerCycle();
		this.iconsPerCycle = (ProgressIndicator.allowTaskQueueing ?
            Dyn.plotUtil.stdIconsPerCycle :	     // standard number of icons per cycle
            ProgressIndicator.allIconsPerCycle );	// don't queue

        if( taskMultiplier > 1 ) {
            this.iconsPerCycle = Math.round( this.iconsPerCycle * taskMultiplier );
        }
        this.debugInitialIconsPerCycle = this.iconsPerCycle;
        //console.log("queueTask multiplier is "+taskMultiplier+" iconsPerCycle is "+this.iconsPerCycle );
        Dyn.assert( isFinite( this.iconsPerCycle ));

		// determine  if we want to queue a task or just ask caller to run it right away
		queueIt = ProgressIndicator.allowTaskQueueing && ( nonIconTask || (totalNumIcons > this.iconsPerCycle));
		if( queueIt )
			this.taskQueue.push( taskFunction );
		return queueIt;
	}

	queueStopFunction( stopFunction, reasonString ) {
		Dyn.assert( typeof stopFunction === "function" );
        if( this.stopFunction && reasonString !== this.stopFunction.stopFunctionReason ) {
            // finish old stop function if not the same as the current stop function
            Dyn.debugging && console.log('progressIndicator', this.stopFunction.stopFunctionReason, 'then queueStopFunction()', reasonString);
            this.stopFunction();
            this.stopFunction = null;
        }
		this.stopFunction = stopFunction;
		this.stopFunction.stopFunctionReason = reasonString;
	}

	/**
	 * @param {number} [delayStopByMS] -- optional delay in milliseconds before stopping
	 */
	setStopDelay( delayStopByMS ) {
		this.stopDelayMS = delayStopByMS || 0;
		//console.log("Dyn.ProgressIndicator SetStopDelay()="+this.stopDelayMS+" at "+this.elapsedTime());
	}

	/**
	 * Stop the progress meter and task now, or after a fixed time, depending on setStopDelay().
	 * The delay is to coordinate the ending of the progress meter at approximately the same
	 * time as another animation of the same length.
	 * @param {string} [reasonString] -- reason for stop, for debugging only
	 */
	stop( reasonString, isCancel ) {
		var prog = this;

		if( isCancel ) {
			// if cancelling, always stop immediately
			this.setStopDelay( 0 );
		}
		if( this.stopDelayMS > 0 ) {
			// set up a timer to stop after some delay, so that we stay in sync with other animation
			// delays that are typical when a progressIndicator is needed.
            this.stopTimers.push( setTimeout( stopNow, this.stopDelayMS )); // just to show we have a timer running
		} else {
			stopNow();
		}

		function stopNow() {
            var //thisStartMS = prog.startTimeMS,
                elapsedTimeMS   = prog.elapsedTime(),
                lastTimeMS      = ( Date.now() - prog.lastStartMS ),
                wasRunning      = ( prog.startTimeMS > 0 ),
                fractionWasDone = Number( prog.fractionDone ).toFixed( 2 );
            //hadTask =( prog.taskFunction !== null);

            if( prog.stopFunction ) {
                //console.log("progressIndicator", prog.stopFunction.stopFunctionReason, 'then stop()' );
                prog.stopFunction(); // run the stop function given by the plot view
                prog.stopFunction = null;
            }
            Dyn.mediator.publish( "plot:indicateProgress", {
                progress: -1
            });

            do {
                clearTimeout( prog.cycleTimers.pop());
            } while ( prog.cycleTimers.length );
            do {
                clearTimeout( prog.stopTimers.pop());
            } while ( prog.stopTimers.length );
			prog.startTimeMS = 0; // this causes elapsedTime() to greatly increase, timing out animations if any
			prog.stopDelayMS = 0; // if stop called twice, 2nd one will not have delay.
			prog.taskQueue = [];
			prog.fractionDone = 0;

			if( prog.debugCyclesWithTasksCount && ProgressIndicator.debugTaskEnd ) {
                console.log(
                    "progressIndicator stop()" +
                    " at " + (wasRunning ? elapsedTimeMS : lastTimeMS) + " ms " +
                    " workingCycles:" + prog.debugCyclesWithTasksCount + " of " + (prog.cycleNumber - 0) +
                    " iconsPerCycle: " + prog.debugInitialIconsPerCycle + "-" + prog.iconsPerCycle +
                    " fractionDone: " + fractionWasDone +
                    " immediateIndicator:" + Boolean( prog.flags.showImmediateProgress ) +
                    " reason:'" + reasonString + "'"
                );
            }
			if( Dyn.debugging && Dyn.debugSVG ) {
				Dyn.logSizeOfSVGDoc();
			}
		}
	}

} // class ProgressIndicator
Dyn.ProgressIndicator = ProgressIndicator;

/**
 * The plot animator coordinates the animation timings of the plot view
 * and various components that need to stay in sync, which are:
 * plotView.progressIndicator: Dyn.ProgressIndicator() that ends at the same time as plot change animations
 * plotView.adornments: controlled by calcAdornments() for various derived classes of PlotElement, should end at the same time as other animations
 */
class PlotAnimator {
    constructor() {
        this.initTimings();
    }

    destroy() {
    }

    initTimings( noAnimation /* optional */, wantFastCycles /* optional */) {
        // animation parameters used by Raphael .animate()
        this.animationTime = Dyn.plotUtil.stdAnimationTimeMs;
        this.animationEasing = Dyn.plotUtil.stdAnimationEasing;
        // variables used to determine timings
        this.noAnimation = Boolean( noAnimation );
        this.wantFastCycles = Boolean( wantFastCycles );
        this.taskAcceleration = 1; // up to 10x acceleration
        this.totalNumIconsToUpdate = 0; // icon total for task accelerator
        this.appearingIconsCount = 0;
        this.appearingIconsTotal = 0;
        this.visibleIconsCount = 0;
        this.visibleIconsTotal = 0;
    }

    setIconTotals( numIcons, visibleIcons, numPositions, appearingPositions ) {

        this.appearingIconsCount = appearingPositions;
        this.appearingIconsTotal = numPositions;
        this.visibleIconsCount = visibleIcons; // icons already visible which may be zero
        this.visibleIconsTotal = numIcons;     // icons created which may be zero on first load

        this.totalNumIconsToUpdate = numPositions; // for task accelerator count
    }

    setTaskAcceleration( doMorePerTask ) {
        // fractionVisible: if all icons visible return 1.0, if half of icons are null (by removeInvisibleIcons()), return 0.5, etc.
        let visibleOrAppearingIconsTotal = Math.max( this.visibleIconsTotal, this.appearingIconsTotal ),
            visibleOrAppearingIconsCount = Math.max( this.visibleIconsCount, this.appearingIconsCount ),
            fractionVisible = Dyn.mathUtil.clipToMinMax( 0.0001, 1, (visibleOrAppearingIconsTotal > 0) ? (visibleOrAppearingIconsCount / visibleOrAppearingIconsTotal) : 0); // 0 < fraction <= 1
        let accel = 1,
            maxAcceleration = Dyn.plotUtil.maxIconCycleMultiplier;
        if( this.noAnimation ) {
            accel = Dyn.plotUtil.maxIconCycleMultiplier;
        } else if( this.wantFastCycles || doMorePerTask ) {
            accel = Dyn.plotUtil.fastIconCycleMultiplier;
        }
        this.taskAcceleration = Dyn.mathUtil.clipToMinMax( 1, maxAcceleration, accel * Dyn.mathUtil.invertSafely( fractionVisible )); // up to 10x acceleration
        Dyn.debugPlotUpdates && console.log( "setTaskAcceleration taskAcceleration:"+this.taskAcceleration+" fractionVisible:"+fractionVisible+" visibleIconsTotal:"+this.visibleIconsTotal );

        return this.taskAcceleration;
    }

    setTimingsForIconSelection() {
        this.animationTime = Dyn.plotUtil.quickAnimationTimeMs;
        this.animationEasing = Dyn.plotUtil.quickAnimationEasing;
    }
    setTimingsForIconColorChange() {
        this.animationTime = Dyn.plotUtil.quickAnimationTimeMs;
        this.animationEasing = Dyn.plotUtil.quickAnimationEasing;
    }
    setTimingsForIconPositionChange( isMixupTask ) {
        if( this.noAnimation ) {
            this.animationTime    = Dyn.plotUtil.noAnimationMs; //override standard animation timing
            this.taskAcceleration = Dyn.mathUtil.clipToMinMax( Dyn.plotUtil.fastIconCycleMultiplier, Dyn.plotUtil.maxIconCycleMultiplier, this.taskAcceleration ); // no less than 3x acceleration;
        } else if( isMixupTask ) {
            this.animationTime = Math.round( Dyn.plotUtil.mixupAnimationTimeMs / Math.max(1, this.totalNumIconsToUpdate / Dyn.plotUtil.mixupIconsPerCycle ));
            this.animationEasing = Dyn.plotUtil.mixupAnimationEasing;
        } else if( this.wantFastCycles ) {
            this.animationTime   = Dyn.plotUtil.quickAnimationTimeMs;
            this.animationEasing = Dyn.plotUtil.quickAnimationEasing;
        } else {
            this.animationTime = Dyn.plotUtil.stdAnimationTimeMs;
            this.animationEasing = Dyn.plotUtil.stdAnimationEasing;
        }
    }

} // class PlotAnimator
Dyn.PlotAnimator = PlotAnimator;

} // wrapDynProgressIndicator
